import React from "react";
import { Grid } from "semantic-ui-react";
import Flex from "@common/components/shared/Flex";
import { useDeviceDetection } from "@common/components/hooks/useDeviceDetection";

/**
 * Reusable layout component that displays main content and detail content
 * side-by-side on desktop, or stacked vertically on tablet/mobile
 *
 * @param {React.ReactNode} mainContent - Content to display on left (desktop) or top (tablet)
 * @param {React.ReactNode} detailContent - Content to display on right (desktop) or bottom (tablet)
 * @param {boolean} showDetails - Controls whether detail content is visible
 * @param {React.ReactNode} filters - Optional filters to display above detail content
 * @param {number} mainColumnWidth - Width of main column on desktop (default: 8)
 * @param {number} detailColumnWidth - Width of detail column on desktop (default: 8)
 */
const TableDetailLayout = ({
  mainContent,
  detailContent,
  showDetails,
  filters = null,
  mainColumnWidth = 8,
  detailColumnWidth = 8,
}) => {
  const isDesktop = useDeviceDetection();

  if (isDesktop) {
    // If showDetails is false, stretch main content to full width
    if (!showDetails) {
      return (
        <Grid padded={false} className="d-flex flex-direction-row flex-grow-1">
          <Grid.Row>
            <Grid.Column
              width={16}
              className="d-flex flex-direction-column"
            >
              {mainContent}
            </Grid.Column>
          </Grid.Row>
        </Grid>
      );
    }

    // If showDetails is true, use side-by-side layout
    return (
      <Grid padded={false} className="d-flex flex-direction-row flex-grow-1">
        <Grid.Row>
          <Grid.Column
            width={mainColumnWidth}
            style={{ paddingRight: 0 }}
            className="d-flex flex-direction-column"
          >
            {mainContent}
          </Grid.Column>
          <Grid.Column
            width={detailColumnWidth}
            className="d-flex flex-direction-column"
          >
            {filters}
            {detailContent}
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }

  // Tablet/Mobile: Vertical stack (original behavior)
  return (
    <Flex>
      {mainContent}
      {showDetails && filters}
      {showDetails && detailContent}
    </Flex>
  );
};

export default TableDetailLayout;

